import Button from 'components/Form/Button';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { POST } from 'services/api';
import { optionsPrazo, statusBanco } from 'utils/options';
import { parseCurrency } from 'utils/parsers';
import FormCore from '../../Form/Core';
import Wrapper from '../Wrapper';
import { AddProposalContainer, CompanyButtonContainer } from './styled';

export default function ModalPreanalise({
  dados,
  preProposalId,
  reload,
  onClose,
}) {
  const formRef = useRef(null);
  const isMounted = useRef(true);
  const [loading, setLoading] = useState(false);
  const valorTotalVenda = dados?.valor_venda
    ? Number(dados.valor_venda.toFixed(2))
    : 0;
  const toNumber = useCallback((value) => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    const num = parseFloat(
      String(value)
        .replace(/[^\d,-]/g, '')
        .replace('.', '')
        .replace(',', '.')
    );
    return isNaN(num) ? 0 : num;
  }, []);
  // Atualiza valor financiado automaticamente
  const atualizarValorFinanciado = useCallback(() => {
    if (!formRef.current || valorTotalVenda === 0) {
      return;
    }
    const entradaRaw = formRef.current.getItem?.('valor_entrada') || '0';
    const entrada = toNumber(entradaRaw);
    const financiamento = Math.max(0, valorTotalVenda - entrada);
    formRef.current.setItem?.(
      parseCurrency(financiamento.toFixed(2)),
      'valor_financiamento'
    );
  }, [valorTotalVenda, toNumber]);

  // Preenche todos os campos AO MONTAR o modal
  useEffect(() => {
    if (!dados || !formRef.current?.setItem) return;
    // 1. Valor total da venda
    formRef.current.setItem(
      parseCurrency(valorTotalVenda.toFixed(2)),
      'valor_venda'
    );
    // 2. Valor de entrada (o mais importante!)
    const entradaExistente = dados.valor_entrada || dados.valorEntrada || 0;
    const entradaFormatada = entradaExistente
      ? parseCurrency(toNumber(entradaExistente).toFixed(2))
      : '0,00';
    formRef.current.setItem(entradaFormatada, 'valor_entrada');
    // 3. Valor financiado (calculado corretamente com entrada)
    const financiamentoInicial = Math.max(
      0,
      valorTotalVenda - toNumber(entradaExistente)
    );
    formRef.current.setItem(
      parseCurrency(financiamentoInicial.toFixed(2)),
      'valor_financiamento'
    );
    // 4. Outros campos
    if (dados.prazo_financiamento) {
      formRef.current.setItem(String(dados.prazo_financiamento), 'parcelas');
    }
    if (dados.valor_parcelas) {
      formRef.current.setItem(
        parseCurrency(toNumber(dados.valor_parcelas).toFixed(2)),
        'valor_parcelas'
      );
    }
    if (dados.data) {
      const date = new Date(dados.data);
      if (!isNaN(date.getTime())) {
        formRef.current.setItem(date.toISOString().split('T')[0], 'data');
      }
    }
    if (dados.status) {
      formRef.current.setItem(dados.status, 'status');
    }
    // Força atualização visual imediata (evita delay)
    setTimeout(() => {
      if (formRef.current?.forceUpdate) formRef.current.forceUpdate();
    }, 50);
  }, [dados, valorTotalVenda, toNumber]);
  const formItems = [
    {
      ref: 'valor_venda',
      placeholder: 'Valor Total da Venda (R$)',
      half: true,
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_entrada',
      placeholder: 'Valor Entrada (R$)',
      half: true,
      type: 'money',
      onChange: atualizarValorFinanciado,
      onBlur: atualizarValorFinanciado,
      required: false,
    },
    {
      ref: 'valor_financiamento',
      placeholder: 'Valor Financiado (R$)',
      half: true,
      type: 'money',
      disabled: true,
      transparent: false,
    },
    {
      ref: 'valor_parcelas',
      placeholder: 'Valor das Parcelas (R$)',
      half: true,
      type: 'money',
      required: false,
    },
    {
      ref: 'data',
      placeholder: 'Data da Primeira Parcela',
      half: true,
      type: 'date',
      required: false,
    },
    {
      ref: 'parcelas',
      placeholder: 'Número de Parcelas',
      half: true,
      type: 'select',
      options: optionsPrazo,
      required: false,
    },
    {
      ref: 'status',
      placeholder: 'Status',
      full: true,
      type: 'select',
      options: statusBanco,
      required: true,
    },
  ];
  const save = async () => {
    //if (!formRef.current) return toast.error('Erro no formulário');
    const form = await formRef.current.getForm?.();
    if (!form) return;
    const entrada = toNumber(form.valor_entrada);
    const financiamento = Math.max(0, valorTotalVenda - entrada);
    const valorParcelas = toNumber(form.valor_parcelas);
    const parcelas = Number(form.parcelas) || 0;
    if (
      form.status.toLowerCase().trim() === 'aprovado' ||
      form.status.toLowerCase().trim() === 'em analise'
    ) {
      if (entrada > valorTotalVenda)
        return toast.error('Entrada maior que o valor total');
      if (valorParcelas <= 0 || parcelas <= 0)
        return toast.error('Preencha valor e número de parcelas');
      //verifica se a data e menor que a data atual
      if (form.data) {
        const dataSelecionada = new Date(form.data);
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        if (dataSelecionada < hoje) {
          return toast.error(
            'A data da primeira parcela não pode ser menor que a data atual.'
          );
        }
      } else {
        return toast.error('A data da primeira parcela é obrigatória.');
      }
      // const totalCalculado = valorParcelas * parcelas;
      // if (Math.abs(totalCalculado - financiamento) > 10) {
      //   return toast.error('O total das parcelas não bate com o valor financiado teste');
      // }
    }
    if (!form) return toast.error('Não foi possível ler os dados');
    const idPreProposta = preProposalId ? Number(preProposalId) : null;
    if (!idPreProposta || isNaN(idPreProposta)) {
      return toast.error('ID da proposta inválido.');
    }
    setLoading(true);
    const payload = {
      id: idPreProposta,
      banco: {
        id: dados.id,
        status: form.status.toLowerCase().trim(),
        valor_parcelas: Number(valorParcelas.toFixed(2)) || 0,
        valor_financiamento: Number(financiamento.toFixed(2)) || 0,
        valor_entrada: Number(entrada.toFixed(2)) || 0,
        prazo_financiamento: String(parcelas) || null,
        data: form.data,
      },
    };
    try {
      await POST('/pre-proposal/atualizabanco', payload, true);
      toast.success('Dados bancários atualizados com sucesso!');
      reload?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  if (!dados) {
    return null;
  }

  return (
    <Wrapper onClose={onClose}>
      <AddProposalContainer>
        <FormCore ref={formRef} formItems={formItems} />
      </AddProposalContainer>
      <CompanyButtonContainer>
        <Button onClick={onClose} color="lightgrey" border nospace>
          Cancelar
        </Button>
        <Button color="blue" loading={loading} onClick={save} nospace>
          Salvar Alterações
        </Button>
      </CompanyButtonContainer>
    </Wrapper>
  );
}
